import { useState, useMemo, useEffect } from 'react';
import { Popup, Anchor, useMap } from 'react-map-gl';
import { useAtom } from 'jotai';
import { Loading } from '@carbon/react';
import { Pipeline, PipelineQueryResponse } from 'types/api-responses';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import baseStyle from './map.module.scss';
import pipelineStyle from './pipeline-popup.module.scss';

interface PipelinePopupProps {
  closeButton?: boolean;
  onClose: () => void;
  latitude: number;
  longitude: number;
  uspipId: number;
  pipelineName?: string; // Initial pipeline name from map data
}

interface PipelineQueryParams extends SearchParams {
  uspipId: number;
}

const createPipelinePopupSearchAtom = (uspipId: number) => {
  return searchAtomPair<PipelineQueryResponse, PipelineQueryParams>(
    { href: '/queries/pipelines' },
    {
      uspipId: uspipId,
    }
  );
};

const PipelinePopup = ({
  closeButton = true,
  onClose,
  latitude,
  longitude,
  uspipId,
  pipelineName,
}: PipelinePopupProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentUspipId, setCurrentUspipId] = useState<number | null>(null);

  // Popup positioning
  const { current: map } = useMap();
  const [anchor, setAnchor] = useState<Anchor>('right');

  const searchAtoms = useMemo(
    () => createPipelinePopupSearchAtom(uspipId),
    [uspipId]
  );
  const [searchData, triggerSearch] = useAtom(searchAtoms.searchAtom);

  const pipeline = useMemo(() => {
    const pipelineData = searchData.data?.results?.[0];
    return pipelineData || null;
  }, [searchData.data?.results]) as Pipeline | null;

  useEffect(() => {
    if (currentUspipId === uspipId && searchData.data?.results?.length) {
      return;
    }

    const timeout = setTimeout(() => {
      triggerSearch(() => ({
        page: 1,
        pageSize: 1,
        uspipId: uspipId,
      }));
      setCurrentUspipId(uspipId);
      setIsExpanded(true);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    uspipId,
    currentUspipId,
    searchData.data?.results?.length,
    triggerSearch,
  ]);

  // Calculate popup anchor position
  useEffect(() => {
    if (!map || !isExpanded) return;

    const mapContainer = map.getContainer();
    const { width, height } = mapContainer.getBoundingClientRect();
    const point = map.project([longitude, latitude]);
    const popupWidth = 450; // Match the actual popup width (400-500px)
    const popupHeight = 400; // Increased to account for the content height
    const buffer = 10;
    const spaceOnRight = width - point.x - buffer;
    const spaceOnBottom = height - point.y - buffer;

    let horizontalAnchor: string;
    if (spaceOnRight < popupWidth) {
      horizontalAnchor = 'right';
    } else {
      horizontalAnchor = 'left';
    }

    let verticalAnchor: string;
    if (spaceOnBottom < popupHeight) {
      verticalAnchor = 'bottom';
    } else {
      verticalAnchor = 'top';
    }

    const finalAnchor: Anchor = verticalAnchor
      ? (`${verticalAnchor}-${horizontalAnchor}` as Anchor)
      : (horizontalAnchor as Anchor);

    setAnchor(finalAnchor);
  }, [map, longitude, latitude, isExpanded]);

  if (searchData.error) {
    return (
      <Popup
        key={Number(closeButton)}
        closeButton={closeButton}
        closeOnClick={false}
        onClose={onClose}
        latitude={latitude}
        longitude={longitude}
        anchor={anchor}
        className={`${baseStyle.popup} ${baseStyle.pipelinePopup}`}
      >
        <div className={pipelineStyle.popupContainer}>
          <h3>Error</h3>
          <p>{searchData.error}</p>
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      key={Number(closeButton)}
      closeButton={closeButton}
      closeOnClick={false}
      onClose={onClose}
      latitude={latitude}
      longitude={longitude}
      anchor={anchor}
      className={`${baseStyle.popup} ${baseStyle.pipelinePopup} ${
        !isExpanded ? baseStyle.pipelinePopupCollapsed : ''
      }`}
    >
      <div className={pipelineStyle.popupHeader}>
        {!isExpanded && (
          <div>
            <p className={pipelineStyle.pipelineTitle}>
              {pipelineName || `Pipeline ${uspipId}`}
            </p>
          </div>
        )}
      </div>
      <div className={pipelineStyle.popupContainer}>
        {isExpanded && (
          <>
            <div className={pipelineStyle.pipelineHeader}>
              <div>
                <h3 className={pipelineStyle.pipelineTitle}>
                  {pipeline?.pipelineOperator}
                </h3>
              </div>
            </div>
            {searchData.loading && (
              <div className={pipelineStyle.loading}>
                <Loading small description="Loading pipeline data..." />
              </div>
            )}

            {!searchData.loading && pipeline && (
              <>
                <div className={pipelineStyle.pipelineContainer}>
                  <div className={pipelineStyle.pipelineDetails}>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Operator:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.pipelineOperator || '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        System Name:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.systemName || '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Subsystem Name:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.subSystemName || '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Commodity Description:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.commodityDescription || '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Category:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.category || 'Unknown'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>Type:</span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.type || '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Capacity:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.capacity || '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Status:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.status || '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Diameter:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.diameterIn
                          ? `${pipeline.diameterIn} Inch`
                          : '--'}
                      </span>
                    </div>
                    <div className={pipelineStyle.pipelineRow}>
                      <span className={pipelineStyle.pipelineLabel}>
                        Length:
                      </span>
                      <span className={pipelineStyle.pipelineValue}>
                        {pipeline.lengthMiles
                          ? `${pipeline.lengthMiles} Miles`
                          : '--'}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!searchData.loading && !pipeline && currentUspipId === uspipId && (
              <div className={pipelineStyle.noPipelineMessage}>
                No pipeline data found.
              </div>
            )}
          </>
        )}
      </div>
    </Popup>
  );
};

export { PipelinePopup };
